import { useEffect, useRef } from "react";
import { HighlightColours } from "../../../models/HighlightColors";
import { HighlightObjectModified } from "../../../models/HighlightObject";
import { HighlightObjectFactory } from "../../../models/HighlightObjectFactory";
import { KeyValueMatcher } from "../../../models/matchers/KeyValueMatcher";
import { Section } from "../../../models/matchers/Section";
import { useHighlightDispatch } from "../../highlightProvider/HighlightProvider";

export { useKeyValueHighlight };

function useKeyValueHighlight(
  valueColour: number,
  matcher: KeyValueMatcher,
  highlightKey: string,
  showHighlights: boolean,
  coordinatesModified: (
    newCoordinates: HighlightObjectModified,
    type: "KEY" | "VALUE",
  ) => void,
) {
  const highlightDispatch = useHighlightDispatch();
  // define different highlight key for Key and Value highlights
  const keyHighlightKey = `${highlightKey}-KEY`;
  const valueHighlightKey = `${highlightKey}-VAL`;

  const coordinatesModifiedRef = useRef(coordinatesModified);
  coordinatesModifiedRef.current = coordinatesModified;

  function dispatchHighlight(
    type: "add" | "remove",
    highlightKey: string,
    color: string,
    section: Section,
    modifiedCallback: (newCoordinates: HighlightObjectModified) => void,
  ) {
    highlightDispatch({
      type,
      highlight: HighlightObjectFactory.createSectionHighlight(
        highlightKey,
        color,
        section,
        modifiedCallback,
        true,
      ),
    });
  }

  function addHighlight(
    highlightKey: string,
    color: string,
    section: Section,
    modifiedCallback: (newCoordinates: HighlightObjectModified) => void,
  ) {
    dispatchHighlight("add", highlightKey, color, section, modifiedCallback);
  }
  function removeHighlight(
    highlightKey: string,
    color: string,
    section: Section,
  ) {
    dispatchHighlight("remove", highlightKey, color, section, (x: any) => null);
  }

  useEffect(() => {
    if (!showHighlights) {
      removeHighlight(
        keyHighlightKey,
        HighlightColours.getKvKeyColor(),
        matcher.keyMatcher.section,
      );
      removeHighlight(
        valueHighlightKey,
        HighlightColours.getHighlightColor(valueColour),
        matcher.valueMatcher.section,
      );
      return;
    }

    const stableCallbackKey = (newCoordinates: HighlightObjectModified) => {
      coordinatesModifiedRef.current(newCoordinates, "KEY");
    };
    const stableCallbackVal = (newCoordinates: HighlightObjectModified) => {
      coordinatesModifiedRef.current(newCoordinates, "VALUE");
    };

    addHighlight(
      keyHighlightKey,
      HighlightColours.getKvKeyColor(),
      matcher.keyMatcher.section,
      stableCallbackKey,
    );

    addHighlight(
      valueHighlightKey,
      HighlightColours.getHighlightColor(valueColour),
      matcher.valueMatcher.section,
      stableCallbackVal,
    );
    return () => {
      removeHighlight(
        keyHighlightKey,
        HighlightColours.getKvKeyColor(),
        matcher.keyMatcher.section,
      );
      removeHighlight(
        valueHighlightKey,
        HighlightColours.getHighlightColor(valueColour),
        matcher.valueMatcher.section,
      );
    };
  }, [
    showHighlights,
    keyHighlightKey,
    valueHighlightKey,
    valueColour,
    matcher.keyMatcher.section.getSectionId(),
    matcher.valueMatcher.section.getSectionId(),
    highlightDispatch,
  ]);
}
